import {
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  TableCell,
  Checkbox,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { withStyles } from '@mui/styles';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { TaskManagementFilter } from 'src/models/timesheet';
import { TableColumnCell } from 'src/components/TableColumnCell';
import ProjectSelect from './common/ProjectSelect';
import { useForm } from 'react-hook-form';
import TaskIdMultiSelect from './common/TaskIdMultiSelect';
import MultiSelect from '../common/MultiSelect';
import { compareObjects } from 'src/utils/validate';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import CreateTaskRow from './common/CreateTaskRow';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import TaskModal from './common/TaskModal';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { AppActions } from 'src/store/AppReducer';
import {
  createTask,
  deleteTasks,
  getAllTasks,
  updateTask
} from 'src/services/timeManagementService';
import NoRecord from './common/NoRecord';
import moment from 'moment';
import ConfirmationModal from '../common/confirmationModal/ConfirmationModal';
import { useAppSelector } from 'src/hooks/hooks';
import CalendarRangePicker from '../common/CalendarRangePicker';
import TaskTypeMultiSelect from './common/TaskTypeMultiSelect';
import SearchIcon from '@mui/icons-material/Search';
import ReferenceIdMultiSelect from './common/ReferenceIdMultiSelect';

interface TaskManagementViewProps {
  permissions: any;
  tab: string;
  projectsList: any;
  toggleOpen: () => void;
}

export interface TaskManagementViewRef {
  onSubmitToCreate: (data) => void;
  getTasks: () => void;
  project: string;
}

const TaskManagementView = forwardRef<
  TaskManagementViewRef,
  TaskManagementViewProps
>(({ projectsList, toggleOpen }, ref) => {
  const pagination_wrapper_ref = useRef(null);
  const appUser = useSelector((state: RootStateType) => state.appReducer?.user);
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const dispatch = useDispatch();

  let initialFilter = {
    taskId: null,
    taskTypeId: null,
    status: null,
    startDate: null,
    endDate: null,
    referenceId: null,
    isSubmitted: false,
    limit: 10,
    offset: 0
  } as TaskManagementFilter;

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [filter, setFilter] = useState(initialFilter as TaskManagementFilter);
  const [prevFilter, setPrevFilter] = useState(
    initialFilter as TaskManagementFilter
  );
  const [toggleFilter, setToggleFilter] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(true);
  const [isClearDisabled, setIsClearDisabled] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isCreateRow, setIsCreateRow] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [checkAll, setCheckAll] = useState(false);
  const [checkList, setCheckList] = useState([]);
  const [taskOpen, setTaskOpen] = useState(false);
  const [modifyData, setModifyData] = useState(null);
  const [total, setTotal] = useState<number>(0);
  const [project, setProject] = useState('');
  const [openConfirmationDelete, setOpenConfirmationDelete] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState<any>(null);
  const [searchKeywords, setSearchKeywords] = useState('');

  const toggleConfirmationDelete = () => {
    setOpenConfirmationDelete(!openConfirmationDelete);
  };

  useEffect(() => {
    if (projectsList.length > 0) {
      setProject(projectsList[0]?.projectId);
    }
  }, [projectsList]);

  const { register, handleSubmit, reset, setValue } =
    useForm<TaskManagementFilter>({
      defaultValues: initialFilter
    });

  const handlePageChange = (event: any, newPage: number): void => {
    setFilter({
      ...prevFilter,
      offset: newPage,
      limit: filter.limit,
      isSubmitted: true
    });
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setFilter({
      ...prevFilter,
      limit: parseInt(event.target.value),
      isSubmitted: true
    });
  };

  const onSubmit = () => {
    const newFilter = {
      ...filter,
      isSubmitted: true,
      offset: 0,
      limit: 10
    };

    if (isFilterChanged) {
      setFilter(newFilter);
      setPrevFilter(newFilter);
      setIsFilterChanged(false);
    }
  };


  //clear the filter
  const resetFilterResult = () => {
    reset();

    const result = compareObjects(initialFilter, filter, 'isSubmitted');
    !result &&
      setFilter({
        ...initialFilter,
        endDate: null,
        startDate: null,
        isSubmitted: true
      });

    setPrevFilter({
      ...initialFilter,
      endDate: null,
      startDate: null,
      isSubmitted: true
    });
  };

  useEffect(() => {
    const newFilter = {
      endDate: filter.endDate,
      startDate: filter.startDate,
      taskId: filter.taskId,
      taskTypeId: filter.taskTypeId,
      status: filter.status,
      referenceId: filter.referenceId
    };

    const isNullish = Object.values(newFilter).every((value) => {
      if (value === null) {
        return true;
      }

      return false;
    });

    if (Object.keys(newFilter).length === 0 || isNullish) {
      setIsDisabled(true);
      setIsClearDisabled(true);
    } else {
      /* if(filter.startDate && filter.endDate == null){
        setIsDisabled(true);
      } else { */
      setIsDisabled(false);
      /* } */
      setIsClearDisabled(false);
    }
  }, [filter]);

  //create task by click row
  const handleOnCreate = (taskDescription) => {
    dispatch(AppActions.showLoader());
    const payload = {
      projectId: project,
      endDate: null,
      startDate: moment(new Date()).format('yyyy-MM-DD'),
      createdBy: appUser.EmployeeId,
      taskName: taskDescription,
      //set default task type GENERIC
      taskTypeId: 4,
      status: 'OPENED',
      templateTaskId: null,
      referenceId: null
    };
    createTask([payload])
      .then((res) => {
        if (res.data?.statusCode === 403) {
          setSnackBarMessage('This activity name already exists.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
          dispatch(AppActions.hideLoader());
        } else {
          setSnackBarMessage(res.data?.message ? res.data?.message : 'You’ve created the activity successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          setFilter({
            ...filter,
            offset: 0,
            limit: 10,
            isSubmitted: true
          });
          setIsCreateRow(false);
        }
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage(
          error?.request?.response
            ? error?.request?.response
            : 'Unable to create activity'
        );
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  //update task
  const onSubmitToUpdate = (data: any) => {
    dispatch(AppActions.showLoader());
    const payload = {
      taskId: data.taskId,
      endDate: data.endDate ? data.endDate : null,
      startDate: data.startDate,
      taskName: data.taskName,
      taskTypeId: data.taskTypeId,
      status: data.status,
      referenceId: data?.referenceId,
      projectId:project
    };
    updateTask(payload)
    .then((res) => {
      if (res.data?.statusCode === 500) {
        setSnackBarMessage('This activity name already exists.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
        dispatch(AppActions.hideLoader());
      } else {
        getTasks(project);
        setSnackBarMessage('You have updated activity successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        dispatch(AppActions.hideLoader());
        setTaskOpen(!taskOpen);
      }
    })
    .catch((error) => {
      dispatch(AppActions.hideLoader());
      setSnackBarMessage(
        error?.request?.response
          ? error?.request?.response
          : 'Unable to update activity'
      );
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
    });
  };

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3rem'
    }
  })(TableRow);

  const toggleTaskOpen = () => {
    setTaskOpen(!taskOpen);
  };

  const getTasks = (projectId?) => {
    dispatch(AppActions.showLoader());
    const payload = {
      projectId: projectId ? projectId : project,
      endDate: {
        fromDate: filter?.endDate?.fromDate ? moment(filter?.endDate?.fromDate).format('yyyy-MM-DD') : null,
        toDate: filter?.endDate?.toDate ? moment(filter?.endDate?.toDate).format('yyyy-MM-DD') : null
      },
      startDate: {
        fromDate: filter?.startDate?.fromDate ? moment(filter?.startDate?.fromDate).format('yyyy-MM-DD') : null,
        toDate: filter?.startDate?.toDate ? moment(filter?.startDate?.toDate).format('yyyy-MM-DD') : null
      },
      taskId: filter.taskId,
      taskName: searchKeywords,
      taskTypeId: filter.taskTypeId,
      status: filter.status,
      referenceId: filter.referenceId
    };
    getAllTasks(payload, searchKeywords ? 0 : filter.offset, filter.limit)
      .then((res) => {
        const data = res?.data?.data;
        setTasks(data?.taskSummery);
        setTotal(data?.totalCount);
        setDeleteTaskId(null);
        setCheckAll(false);
        setCheckList([]);
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage(
          error?.request?.response
            ? error?.request?.response
            : 'Unable to load tasks'
        );
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  //create task
  const onSubmitToCreate = (data: any) => {
    dispatch(AppActions.showLoader());
    const payload = {
      projectId: project,
      endDate: data.endDate ? data.endDate : null,
      startDate: data.startDate,
      createdBy: appUser.EmployeeId,
      taskName: data.taskName,
      taskTypeId: data.taskTypeId,
      status: data.status,
      templateTaskId: null,
      referenceId: data?.referenceId
    };

    createTask([payload])
      .then((res) => {
        if (res.data?.statusCode === 403) {
          setSnackBarMessage('This activity name already exists.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
          toggleOpen();
          dispatch(AppActions.hideLoader());
        } else {
          setSnackBarMessage(res.data?.message ? res.data?.message : 'You’ve created the activity successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          getTasks(project);
          toggleOpen();
        }
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage(
          error?.request?.response
            ? error?.request?.response
            : 'Unable to create activity'
        );
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  //delete task
  const deleteTask = () => {
    dispatch(AppActions.showLoader());
    const payload = {
      taskIds: checkList.length > 0 ? checkList : [deleteTaskId]
    };
    deleteTasks(payload)
      .then((res: any) => {
        if (res.data?.statusCode === 200) {
          setSnackBarMessage(
            res.data?.message ? res.data?.message :
              `You have deleted the activity  successfully.`
          );
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
        } else {
          setSnackBarMessage(
            res.data?.message
          );
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        }
        getTasks(project);
        setCheckList([]);
        toggleConfirmationDelete();
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage(
          error?.request?.response
            ? error?.request?.response
            : `Unable to delete ${checkList.length > 1 ? 'tasks' : 'task'}`
        );
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  //load task when change projectcode
  useEffect(() => {
    if (project) {
      getTasks(project);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);
  
  useEffect(() => {
    const makeApiCall = async () => {
      if (project) {
        setFilter({
          ...filter,
          offset: 0,
          limit: 10,
          isSubmitted: true
        });
        getTasks(project);
      }
    };

    const triggerCall = setTimeout(() => {
      makeApiCall();
    }, 500);

    return () => clearTimeout(triggerCall);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeywords]);

  //call API for load first time and when click filter button
  useEffect(() => {
    if (filter.isSubmitted) {
      getTasks(project);
      setFilter((prev) => ({
        ...prev,
        isSubmitted: false
      }));
    }
  }, [filter]);

  useImperativeHandle(ref, () => ({
    onSubmitToCreate,
    getTasks,
    project
  }));

  const getTaskStatus = (value) => {
    let status = value;
    if (value === 'OPENED') {
      status = 'Opened';
    }
    if (value === 'CLOSED') {
      status = 'Closed';
    }
    if (value === 'ON_HOLD') {
      status = 'On hold';
    }

    return status;
  };

  return (
    <div className="task-management">
      <div className="task-management-table">
        <Card>
          <Grid sx={{ px: 2, py: 2 }} item xs={12}>
            <div style={{ width: '300px', marginBottom: 15 }}>
              <ProjectSelect
                width="300px"
                list={projectsList}
                value={project}
                onChange={(e: any) => {
                  resetFilterResult();
                  setProject(e.target.value);
                }}
              />
            </div>
            <Divider />
            <div style={{ marginTop: 15 }}>
              <TextField
                sx={{ width: 300 }}
                size="small"
                id="task-name-search-id"
                placeholder="Activity Name"
                onChange={(e) => setSearchKeywords(e.target.value)}
                InputProps={{
                  style: { fontSize: '12px', height: '1.75rem' },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: '#d3d3d3' }}
                        style={{ fontSize: '15px' }}
                      />
                    </InputAdornment>
                  )
                }}
              />
              <Button
                id="btn-setToggleFilter"
                sx={{
                  ml: 1,
                  mt: { xs: 2, md: 0 },
                  color: `${!toggleFilter && 'gray'}`
                }}
                variant="outlined"
                startIcon={<FilterListIcon style={{ fontSize: '15px' }} />}
                onClick={() => {
                  setToggleFilter(!toggleFilter);
                }}
                style={{ height: '1.75rem', fontSize: '12px' }}
              >
                Filters
              </Button>
            </div>
          </Grid>
          {toggleFilter && (
            <Grid px={2} pb={2} style={{ margin: 0 }}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                  container
                  spacing={1}
                  style={{
                    justifyContent: 'between',
                    flexWrap: 'nowrap',
                    alignItems: 'center'
                  }}
                >
                  <Grid item xs={3} width="20%">
                    <TaskIdMultiSelect
                      {...register('taskId')}
                      value={filter?.taskId || []}
                      projectId={project}
                      onChange={(e: any) => {
                        setFilter((prev) => ({
                          ...prev,
                          taskId: e.target.value
                        }));
                        setValue('taskId', e.target.value);
                        setIsFilterChanged(true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} width="20%">
                    <ReferenceIdMultiSelect
                      {...register('referenceId')}
                      value={filter?.referenceId || []}
                      projectId={project}
                      onChange={(e: any) => {
                        setFilter((prev) => ({
                          ...prev,
                          referenceId: e.target.value
                        }));
                        setValue('referenceId', e.target.value);
                        setIsFilterChanged(true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} width="20%">
                    <TaskTypeMultiSelect
                      {...register('taskTypeId')}
                      value={filter?.taskTypeId || []}
                      projectId={project}
                      onChange={(e: any) => {
                        setFilter((prev) => ({
                          ...prev,
                          taskTypeId: e.target.value
                        }));
                        setValue('taskTypeId', e.target.value);
                        setIsFilterChanged(true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} width="20%">
                    <MultiSelect
                      title="Activity Status"
                      dropdownList={[
                        {
                          value: 'OPENED',
                          name: 'Opened'
                        },
                        {
                          value: 'CLOSED',
                          name: 'Closed'
                        },
                        {
                          value: 'ON_HOLD',
                          name: 'On hold'
                        }
                      ]}
                      value={filter?.status || []}
                      onChange={(e: any) => {
                        setFilter((prev) => ({
                          ...prev,
                          status: e.target.value
                        }));
                        setValue('status', e.target.value);
                        setIsFilterChanged(true);
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={1}
                  style={{
                    justifyContent: 'between',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    marginTop: '5px'
                  }}
                >
                  <Grid item xs={3} width="20%">
                      <CalendarRangePicker
                        fullWidth
                        placeholder="Start date"
                        onApply={(event, picker) => {
                          setValue('startDate', {
                            fromDate: picker.startDate,
                            toDate: picker.endDate
                          });

                          setFilter((prev) => ({
                            ...prev,
                            startDate: {
                              fromDate: picker.startDate,
                              toDate: picker.endDate
                            }
                          }));
                          setIsFilterChanged(true);
                        }}
                        fromDate={
                          filter?.startDate?.fromDate &&
                          new Date(filter?.startDate?.fromDate)
                        }
                        toDate={
                          filter?.startDate?.toDate &&
                          new Date(filter?.startDate?.toDate)
                        }
                      />
                    </Grid>
                    <Grid item xs={3} width="20%">
                      <CalendarRangePicker
                        fullWidth
                        placeholder="End date"
                        onApply={(event, picker) => {
                          setValue('endDate', {
                            fromDate: picker.startDate,
                            toDate: picker.endDate
                          });

                          setFilter((prev) => ({
                            ...prev,
                            endDate: {
                              fromDate: picker.startDate,
                              toDate: picker.endDate
                            }
                          }));
                          setIsFilterChanged(true);
                        }}
                        fromDate={
                          filter?.endDate?.fromDate &&
                          new Date(filter?.endDate?.fromDate)
                        }
                        toDate={
                          filter?.endDate?.toDate &&
                          new Date(filter?.endDate?.toDate)
                        }
                      />
                    </Grid>
                  <Grid
                    item
                    xs={3}
                    width="60%"
                    style={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      marginLeft: 'auto',
                      marginTop: '8px',
                      display: 'flex'
                    }}
                  >
                    <Button
                      sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                      variant="outlined"
                      onClick={resetFilterResult}
                      disabled={isClearDisabled}
                      className={isClearDisabled && 'clear-btn'}
                      style={{ height: '1.75rem', fontSize: '12px' }}
                      id="btn-resetFilterResult"
                    >
                      Clear
                    </Button>
                    <Button
                      sx={{ mt: { xs: 2, md: 0 } }}
                      id="btn-onSubmit"
                      variant="outlined"
                      type="submit"
                      disabled={isDisabled}
                      className={isDisabled && 'filter-btn'}
                      style={{ height: '1.75rem', fontSize: '12px' }}
                    >
                      Filter
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          )}
          <Divider />
          {checkList.length > 0 && (
            <Grid
              sx={{ px: 3, py: 1 }}
              item
              xs={12}
              className="selcted-wrapper"
            >
              <Typography
                paddingBottom="0.25rem"
                paddingTop="0.5rem"
                variant="body2"
                style={{ fontSize: '13px' }}
                gutterBottom
                noWrap
              >
                {`${checkList.length} selected`}
              </Typography>
              <DeleteIcon
                onClick={() => {
                  toggleConfirmationDelete();
                }}
                className="bulk-delete-icon"
                fontSize="small"
              />
            </Grid>
          )}
          <TableContainer
            className="tableContainer"
            style={{ height: tasks?.length === 0 || !tasks ? '300px' : 'auto' }}
          >
            <Table
              style={{ borderCollapse: 'separate', position: 'relative' }}
              size="small"
            >
              <TableHead>
                <CustomTableRow
                  style={{ position: 'sticky', top: 0, zIndex: 1 }}
                >
                  <TableCell
                    style={{
                      width: '30px',
                      whiteSpace: 'nowrap',
                      alignItems: 'center',
                      color: '#232C68',
                      fontWeight: '700',
                      fontSize: '11px',
                      background: '#E6E9FF',
                      paddingRight: 0
                    }}
                  >
                    <Checkbox
                      className="check-box"
                      indeterminate={
                        checkList.length > 0 && checkList.length < tasks.length
                      }
                      checked={checkAll}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCheckAll(true);
                          const taskIds = tasks.map((e) => e.taskId);
                          setCheckList(taskIds);
                        } else {
                          setCheckAll(false);
                          setCheckList([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableHeaderCell content="Activity Id" smallFont />
                  <TableHeaderCell content="Reference Id" smallFont />
                  <TableHeaderCell
                    width="300px"
                    content="Activity Name"
                    smallFont
                  />
                  <TableHeaderCell content="Activity Type" smallFont />
                  <TableHeaderCell content="Activity Status" smallFont />
                  <TableHeaderCell content="Start Date" smallFont />
                  <TableHeaderCell content="End Date" smallFont />
                  <TableHeaderCell content="Action" smallFont />
                </CustomTableRow>
              </TableHead>
              <TableBody>
                {isCreateRow ? (
                  <CreateTaskRow
                    onClose={() => {
                      setIsCreateRow(false);
                    }}
                    onCreate={handleOnCreate}
                  />
                ) : (
                  <TableRow hover>
                    <TableColumnCell></TableColumnCell>
                    <TableColumnCell></TableColumnCell>
                    <TableColumnCell>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        onClick={() => {
                          setIsCreateRow(true);
                        }}
                      >
                        <AddTwoToneIcon
                          fontSize="small"
                          className="plus-icon"
                        />
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{
                            fontSize: '12px',
                            color: 'rgba(29,36,51,0.6)'
                          }}
                          gutterBottom
                          noWrap
                        >
                          Create a Activity
                        </Typography>
                      </Box>
                    </TableColumnCell>
                    <TableColumnCell colSpan={5}></TableColumnCell>
                  </TableRow>
                )}

                {tasks &&
                  tasks?.length > 0 &&
                  tasks.map((task, i) => {
                    return (
                      <TableRow
                        className={
                          checkList.includes(Number(task.id))
                            ? 'selected-row'
                            : ''
                        }
                        hover
                        key={task.taskId}
                      >
                        <TableColumnCell>
                          <Grid
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-start"
                            gap={1}
                          >
                            <Checkbox
                              className="check-box"
                              checked={
                                checkList.filter((e) => e === task.taskId)
                                  .length > 0
                                  ? true
                                  : false
                              }
                              onChange={(e) => {
                                let newCheckList = [];
                                if (e.target.checked) {
                                  newCheckList = [...checkList, task.taskId];
                                  setCheckAll(
                                    newCheckList.length ===
                                      tasks.map(() => task.taskId).length
                                      ? true
                                      : false
                                  );
                                } else {
                                  setCheckAll(false);
                                  newCheckList = checkList.filter(
                                    (e) => e !== task.taskId
                                  );
                                }
                                setCheckList(newCheckList);
                              }}
                            />
                          </Grid>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            gutterBottom
                            noWrap
                          >
                            {task.taskId}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            gutterBottom
                            noWrap
                          >
                            {task?.referenceId ? task?.referenceId : '-'}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            gutterBottom
                            noWrap
                          >
                            {task.taskName}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            gutterBottom
                            noWrap
                          >
                            {task.taskTypeName}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            gutterBottom
                            noWrap
                          >
                            {getTaskStatus(task.status)}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {task.startDate}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {task.endDate}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell sticky className="sticky-table-col">
                          <div
                            style={{
                              background: 'white',
                              marginLeft: 'auto'
                            }}
                          >
                            <div>
                              <Tooltip title="Edit" placement="top">
                                <EditIcon
                                  id={'btn-edit-' + i}
                                  onClick={() => {
                                    setModifyData(task);
                                    setTaskOpen(true);
                                  }}
                                  style={{
                                    fontSize: '17px',
                                    cursor: 'pointer',
                                    marginRight: '5px'
                                  }}
                                />
                              </Tooltip>
                              <Tooltip title="Delete" placement="top">
                                <DeleteOutlineIcon
                                  id={'btn-delete-' + i}
                                  onClick={() => {
                                    setDeleteTaskId(task?.taskId);
                                    toggleConfirmationDelete();
                                  }}
                                  style={{
                                    fontSize: '17px',
                                    cursor: 'pointer'
                                  }}
                                />
                              </Tooltip>
                            </div>
                          </div>
                        </TableColumnCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            {(tasks?.length === 0 || !tasks) && <NoRecord />}
          </TableContainer>
          <Box
            p={1}
            ref={pagination_wrapper_ref}
            sx={{ borderTop: '1px solid #eee' }}
          >
            <TablePagination
              sx={{
                fontSize: '12px',
                '.MuiTablePagination-displayedRows': {
                  fontSize: '12px'
                },
                '.MuiTablePagination-selectLabel': {
                  fontSize: '12px'
                }
              }}
              component="div"
              count={total ? total : 0}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              page={filter.offset}
              rowsPerPage={filter.limit}
              rowsPerPageOptions={[10, 20, 30, 40, 50]}
            />
          </Box>
        </Card>
        <TaskModal
          taskData={modifyData}
          open={taskOpen}
          toggle={toggleTaskOpen}
          onSave={onSubmitToUpdate}
          type="UPDATE"
        />
        <SnackBar
          snackBarOpen={snackBarOpen}
          setSnackBarOpen={setSnackBarOpen}
          snackBarMessage={snackBarMessage}
          snackbarSeverity={snackbarSeverity}
        />
        <ConfirmationModal
          open={openConfirmationDelete}
          toggle={toggleConfirmationDelete}
          message={`Are you sure want to delete ${
            checkList.length > 1 ? 'these activities' : 'this activity'
          }?`}
          onConfirm={() => deleteTask()}
          noToggle={toggleConfirmationDelete}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
});

export default TaskManagementView;
